import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/flights/cabin-classes';

/**
 * Map API cabin class to UI format
 * API: { id, code, name, rank, icon, active, ... }
 * UI:  { id, cabinClassCode, cabinClassName, rank, status }
 */
export const mapApiToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    cabinClassCode: api.code ?? '',
    cabinClassName: api.name ?? '',
    rank: api.rank ?? 0,
    status: api.active ? 'active' : 'inactive',
  };
};

/**
 * Map UI cabin class to API format for create/update
 */
export const mapUiToApi = (ui) => ({
  code: String(ui.cabinClassCode ?? ui.code ?? '').toUpperCase(),
  name: String(ui.cabinClassName ?? ui.name ?? ''),
  rank: parseInt(ui.rank, 10) || 0,
  icon: ui.icon ?? null,
  active: ui.status === 'active' || ui.active === true,
});

/**
 * useGetCabinClasses - fetch cabin classes with params
 */
export const useGetCabinClasses = (params = {}) => {
  const {
    page = 1,
    limit = 100,
    search = '',
    status = '',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCabinClasses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (status && status !== 'all') {
        queryParams.status = status === 'active' ? 'Active' : 'Inactive';
      }

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData((res.data || []).map(mapApiToUi));
        if (res.pagination) setPagination(res.pagination);
      } else {
        setError(res.message || 'Failed to load cabin classes');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load cabin classes');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status]);

  useEffect(() => {
    fetchCabinClasses();
  }, [fetchCabinClasses]);

  return { data, pagination, loading, error, refetch: fetchCabinClasses };
};

export const useCreateCabinClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.post(BASE, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to create cabin class' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to create cabin class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useUpdateCabinClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.put(`${BASE}/${id}`, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to update cabin class' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to update cabin class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useDeleteCabinClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to delete cabin class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};
